import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLazyQuery, useMutation } from '@apollo/client';
import FullscreenService from '/imports/ui/components/common/fullscreen-button/service';
import { useIsInfiniteWhiteboardEnabled, useIsPollingEnabled } from '/imports/ui/services/features';
import { PluginsContext } from '/imports/ui/components/components-data/plugin-context/context';
import { POLL_CANCEL, POLL_CREATE } from '/imports/ui/components/poll/mutations';
import {
  PRESENTATION_SET_ZOOM,
  PRESENTATION_SET_PAGE,
  PRESENTATION_SET_PAGE_INFINITE_WHITEBOARD,
  PRESENTATION_SET_WRITERS,
} from '../mutations';
import PresentationToolbar from './component';
import Session from '/imports/ui/services/storage/in-memory';
import {
  updateCurrentStatusPresndPage,
  updatePresPageZoom,
  useMeetingIsBreakout,
} from '/imports/ui/components/app/service';
import useDeduplicatedSubscription from '/imports/ui/core/hooks/useDeduplicatedSubscription';
import { USER_AGGREGATE_COUNT_SUBSCRIPTION } from '/imports/ui/core/graphql/queries/users';
import { courseSelect } from '../../app/context';
import {
  CURRENT_PAGE_WRITERS_QUERY,
  CURRENT_PAGE_WRITERS_SUBSCRIPTION,
} from '../../whiteboard/queries';
import Auth from '/imports/ui/services/auth';
import { useLoadedUserList } from '/imports/ui/core/hooks/useLoadedUserList';

const infiniteWhiteboardIcon = (isinfiniteWhiteboard) => {
  if (isinfiniteWhiteboard) {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M14.6667 3H1.33333C1.14924 3 1 3.14924 1 3.33333V13.3333C1 13.5174
             1.14924 13.6667 1.33333 13.6667H14.6667C14.8508 13.6667 15 13.5174 15 13.3333V3.33333C15
             3.14924 14.8508 3 14.6667 3ZM1.33333 2C0.596954 2 0 2.59695 0 3.33333V13.3333C0 14.0697
             0.596953 14.6667 1.33333 14.6667H14.6667C15.403 14.6667 16 14.0697 16 13.3333V3.33333C16
             2.59695 15.403 2 14.6667 2H1.33333Z"
          fill="#4E5A66"
        />
        <path
          d="M12.875 11.875L9.125 8.125M9.125 8.125L9.125 10.9375M9.125 8.125L11.9375 8.125"
          stroke="#4E5A66"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M3.125 5.125L6.875 8.875M6.875 8.875L6.875 6.0625M6.875 8.875L4.0625 8.875"
          stroke="#4E5A66"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.6667 3H1.33333C1.14924 3 1 3.14924 1 3.33333V13.3333C1 13.5174
             1.14924 13.6667 1.33333 13.6667H14.6667C14.8508 13.6667 15 13.5174 15 13.3333V3.33333C15
             3.14924 14.8508 3 14.6667 3ZM1.33333 2C0.596954 2 0 2.59695 0 3.33333V13.3333C0 14.0697
             0.596953 14.6667 1.33333 14.6667H14.6667C15.403 14.6667 16 14.0697 16 13.3333V3.33333C16
             2.59695 15.403 2 14.6667 2H1.33333Z"
        fill="#4E5A66"
      />
      <path
        d="M9.125 8.125L12.875 11.875M12.875 11.875L12.875 9.0625M12.875 11.875L10.0625 11.875"
        stroke="#4E5A66"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.875 8.875L3.125 5.125M3.125 5.125L3.125 7.9375M3.125 5.125L5.9375 5.125"
        stroke="#4E5A66"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const PresentationToolbarContainer = (props) => {
  const pluginsContext = useContext(PluginsContext);
  const {
    pluginsExtensibleAreasAggregatedState,
    setPluginsExtensibleAreasAggregatedState,
  } = pluginsContext;
  const [presentationSetWriters] = useMutation(PRESENTATION_SET_WRITERS);

  const WHITEBOARD_CONFIG = window.meetingClientSettings.public.whiteboard;

  const {
    userIsPresenter,
    userIsModerator,
    layoutSwapped,
    currentSlideNum,
    presentationId,
    numberOfSlides,
    hasPoll,
    currentSlide,
    currentPresentationPage,
    whiteboardId,
  } = props;

  const handleToggleFullScreen = (ref) => FullscreenService.toggleFullScreen(ref);

  const [stopPoll] = useMutation(POLL_CANCEL);
  const [createPoll] = useMutation(POLL_CREATE);
  const [presentationSetZoom] = useMutation(PRESENTATION_SET_ZOOM);
  const [presentationSetPage] = useMutation(PRESENTATION_SET_PAGE);
  const [presentationSetPageInfiniteWhiteboard] = useMutation(
    PRESENTATION_SET_PAGE_INFINITE_WHITEBOARD,
  );
  // PDF loading state (setter unused while PDF export is disabled)
  const [isPdfLoading] = useState(false);
  const courseDetails = courseSelect((state) => state);
  const isPrepareLesson = courseDetails?.course?.courseDetail?.isPrepareLesson ?? false;

  const { data: whiteboardWritersData } = useDeduplicatedSubscription(
    CURRENT_PAGE_WRITERS_SUBSCRIPTION,
    {
      variables: { pageId: whiteboardId },
      skip: !whiteboardId,
    },
  );

  const whiteboardWriters = whiteboardWritersData?.pres_page_writers || [];
  const hasWBAccess = whiteboardWriters?.some((writer) => writer.userId === Auth.userID);

  const resetSlide = () => {
    const { pageId, num } = currentPresentationPage;
    // Default reset values for zoom
    const resetValues = {
      xOffset: 0,
      yOffset: 0,
      widthRatio: 100,
      heightRatio: 100,
    };
    // Include standard BBB auth fields (see docs/BBB_DATA_CONTRACTS.md)
    const apiPayload = {
      internalMeetingID: Auth?._meetingID,
      externalUserID: Auth?._externUserID,
      sessionId: courseDetails?.course?.courseDetail?.courseSessionId,
      presentationId,
      pageId,
      pageNum: num,
      ...resetValues,
    };
    updatePresPageZoom(apiPayload).catch(() => {});
    presentationSetZoom({
      variables: {
        presentationId,
        pageId,
        pageNum: num,
        ...resetValues,
      },
    });
  };

  const endCurrentPoll = () => {
    if (hasPoll) stopPoll();
  };

  const setPresentationPage = async (pageId) => {
    // await captureCurrentSlide()
    // Continue to next presentation page
    presentationSetPage({
      variables: {
        presentationId,
        pageId,
      },
    });
  };
  // const setPresentationPage = (pageId) => {

  //   createSvgOfCurrentSlide(currentSlideNum)
  //   presentationSetPage({
  //     variables: {
  //       presentationId,
  //       pageId,
  //     },
  //   });

  // };

  // const generatePdfFromSlides = async () => {

  //   setIsPdfLoading(true)
  //   // Show processing toast
  //   const toastId = toast.loading(<><Spinner size="sm" />Generating slides for PDF...</>);
  //   await captureCurrentSlide()

  //   // 2️⃣ Filter slides only for the current presentation

  //   getSlidesData(presentationId).then((response) => {
  //     console.log({ response: response.data })
  //     const slidesData = response.data.data.slides
  //     const slides = slidesData.filter(
  //       (slide) => slide.presentationId === presentationId
  //     );
  //     if (!slides || slides.length === 0) {
  //       toast.update(toastId, {
  //         render: "⚠️ No slides found to generate PDF",
  //         type: "warning",
  //         isLoading: false,
  //         autoClose: 3000,
  //       });
  //       return;
  //     }

  //     // Sort slides in order (just in case they’re not sequential)
  //     const sortedSlides = [...slides].sort((a, b) => a.slideNum - b.slideNum);

  //     // Create a new PDF (landscape mode, custom dimensions)
  //     const pdf = new jsPDF({
  //       orientation: "landscape",
  //       unit: "px",
  //       format: "a4", // or [width, height] for exact control
  //     });

  //     for (let i = 0; i < sortedSlides.length; i++) {
  //       const { imageData, slideNum } = sortedSlides[i];

  //       // Add image (centered and scaled)
  //       const pageWidth = pdf.internal.pageSize.getWidth();
  //       const pageHeight = pdf.internal.pageSize.getHeight();

  //       // For best quality, resize proportionally
  //       const imgProps = pdf.getImageProperties(imageData);
  //       const ratio = Math.min(pageWidth / imgProps.width, pageHeight / imgProps.height);
  //       const imgWidth = imgProps.width * ratio;
  //       const imgHeight = imgProps.height * ratio;
  //       const x = (pageWidth - imgWidth) / 2;
  //       const y = (pageHeight - imgHeight) / 2;

  //       if (i > 0) pdf.addPage();
  //       pdf.addImage(imageData, "PNG", x, y, imgWidth, imgHeight);

  //       console.log(`✅ Added slide ${slideNum} to PDF`);
  //     }

  //     const fileName = `presentation_${new Date().toISOString().replace(/[:.]/g, "-")}.pdf`;
  //     pdf.save(fileName);
  //     // ✅ Update toast on success
  //     toast.update(toastId, {
  //       render: "✅ PDF generated successfully!",
  //       type: "success",
  //       isLoading: false,
  //       autoClose: 3000,
  //     });
  //     console.log(`📄 PDF saved as ${fileName}`);
  //   }).catch((error) => {
  //     console.log({ error })
  //     toast.update(toastId, {
  //       render: "❌ Error while generating PDF",
  //       type: "error",
  //       isLoading: false,
  //       autoClose: 3000,
  //     });
  //   }).finally(() => {
  //     setIsPdfLoading(false)
  //   })

  // };
  const setPresentationPageInfiniteWhiteboard = (infiniteWhiteboard) => {
    const pageId = `${presentationId}/${currentSlideNum}`;
    presentationSetPageInfiniteWhiteboard({
      variables: {
        pageId,
        infiniteWhiteboard,
      },
    });
  };

  useEffect(() => {
    if (userIsPresenter) {
      setPresentationPageInfiniteWhiteboard(true);
    }
  }, [userIsPresenter]);

  const skipToSlide = (slideNum) => {
    const slideId = `${presentationId}/${slideNum}`;
    setPresentationPage(slideId);
    // Include standard BBB auth fields (see docs/BBB_DATA_CONTRACTS.md)
    const apiPayload = {
      internalMeetingID: Auth?._meetingID,
      externalUserID: Auth?._externUserID,
      sessionId: courseDetails?.course?.courseDetail?.courseSessionId,
      type: 'page',
      presentationId,
      currentSlide: slideNum, // API expects currentSlide as integer
      slideId, // Keep for reference
    };
    updateCurrentStatusPresndPage(apiPayload).catch(() => {});
  };

  const previousSlide = () => {
    const prevSlideNum = currentSlideNum - 1;
    if (prevSlideNum < 1) {
      return;
    }
    skipToSlide(prevSlideNum);
  };

  const nextSlide = () => {
    const nextSlideNum = currentSlideNum + 1;
    if (nextSlideNum > numberOfSlides) {
      return;
    }
    skipToSlide(nextSlideNum);
  };

  const startPoll = (pollType, pollId, answers = [], question, isMultipleResponse = false) => {
    Session.setItem('openPanel', 'poll');
    Session.setItem('forcePollOpen', true);
    window.dispatchEvent(new Event('panelChanged'));

    createPoll({
      variables: {
        pollType,
        pollId: `${pollId}/${new Date().getTime()}`,
        secretPoll: false,
        question,
        isMultipleResponse,
        answers,
      },
    });
  };

  const isPollingEnabled = useIsPollingEnabled();
  const meetingIsBreakout = useMeetingIsBreakout();
  const allowInfiniteWhiteboard = useIsInfiniteWhiteboardEnabled();
  // const allowInfiniteWhiteboard = true;
  const { data: countData } = useDeduplicatedSubscription(USER_AGGREGATE_COUNT_SUBSCRIPTION);
  const numberOfJoinedUsers = countData?.user_aggregate?.aggregate?.count || 0;

  const { pageId } = currentPresentationPage;

  const { data: usersData } = useLoadedUserList({ offset: 0, limit: 50 }, (u) => u);

  const users = usersData ?? [];

  const [getWriters] = useLazyQuery(
    CURRENT_PAGE_WRITERS_QUERY,
    {
      variables: { pageId },
      fetchPolicy: 'no-cache',
    },
  );

  const handleWhiteboardAccessChange = async () => {
    try {
      // 1. Get all moderator non-presenters
      const moderatorNonPresenters = users.filter(
        (u) => u.isModerator === true && u.presenter === false,
      );
      const moderatorNonPresenterIds = moderatorNonPresenters.map((u) => u.userId);

      // 2. Fetch existing writers
      const { data } = await getWriters();
      const allWriters = data?.pres_page_writers || [];

      // 3. Get writers for current page
      const currentWriters = allWriters
        .filter((writer) => writer.pageId === pageId)
        .map((writer) => writer.userId); // array of userIds

      // 4. Combine existing writers with moderatorNonPresenterIds if not already present
      const updatedWritersSet = new Set([...currentWriters, ...moderatorNonPresenterIds]);

      // 5. Convert to string array
      const updatedWritersArray = Array.from(updatedWritersSet).map(String);

      // 6. Call API to update writers
      await presentationSetWriters({
        variables: {
          pageId,
          usersIds: updatedWritersArray,
        },
      });
    } catch {
      // Silently handle whiteboard access errors
    }
    // try {
    //   const moderatorNonPresenters = users.filter(
    //     (u) => u.isModerator === true && u.presenter === false
    //   );

    //   // Collect only the userIds
    //   const moderatorNonPresenterIds = moderatorNonPresenters.map((u) => u.userId);

    //   const { data } = await getWriters();
    //   const allWriters  = data?.pres_page_writers || [];
    //   const currentWriters = allWriters?.filter((writer) => writer.pageId === pageId);
    //   console.log({allWriters})

    //   // await presentationSetWriters({
    //   //   variables: {
    //   //     pageId,
    //   //     usersIds: moderatorNonPresenterIds,
    //   //   },
    //   // });

    // } catch (error) {
    //   console.log({ error })
    //   // logger.warn(
    //   //   { logCode: 'user_action_whiteboard_access_failed' },
    //   //   'Error updating whiteboard access.',
    //   // );
    // }
  };
  React.useEffect(() => {
    // currentSlideNum

    if (userIsPresenter && usersData && usersData?.length > 0) {
      handleWhiteboardAccessChange();
    }

    // Only proceed if userIsPresenter and usersData is available
  }, [pageId, userIsPresenter, usersData?.length]);

  const firstCallDoneRef = React.useRef(false);

  React.useEffect(() => {
    if (!userIsPresenter) return;

    // First time: need usersData
    if (!firstCallDoneRef.current) {
      if (!usersData || usersData.length === 0) return; // wait until users are loaded
      handleWhiteboardAccessChange();
      firstCallDoneRef.current = true; // mark first call done
    }

    // Do NOT run handleWhiteboardAccessChange again
  }, [usersData]);

  //
  // if ((userIsPresenter) && !layoutSwapped)
  if ((userIsPresenter || hasWBAccess) && !layoutSwapped) {
    // Only show controls if user is presenter and layout isn't swapped

    const pluginProvidedPresentationToolbarItems = pluginsExtensibleAreasAggregatedState
      ?.presentationToolbarItems;
    return (
      <PresentationToolbar
        {...props}
        amIPresenter={userIsPresenter}
        amIModerator={userIsModerator}
        hasWBAccess
        endCurrentPoll={endCurrentPoll}
        isPollingEnabled={isPollingEnabled}
        allowInfiniteWhiteboardInBreakouts={
          WHITEBOARD_CONFIG?.allowInfiniteWhiteboardInBreakouts
        }
        allowInfiniteWhiteboard={allowInfiniteWhiteboard}
        setPluginsExtensibleAreasAggregatedState={
          setPluginsExtensibleAreasAggregatedState
        }
        pluginsExtensibleAreasAggregatedState={
          pluginsExtensibleAreasAggregatedState
        }
        // TODO: Remove this
        isMeteorConnected
        maxNumberOfActiveUsers={WHITEBOARD_CONFIG.maxNumberOfActiveUsers}
        numberOfJoinedUsers={numberOfJoinedUsers}
        // generatePdfFromSlides={generatePdfFromSlides}
        isPdfLoading={isPdfLoading}
        {...{
          pluginProvidedPresentationToolbarItems,
          handleToggleFullScreen,
          startPoll,
          previousSlide,
          nextSlide,
          skipToSlide,
          setPresentationPageInfiniteWhiteboard,
          currentSlide,
          currentPresentationPage,
          infiniteWhiteboardIcon,
          resetSlide,
          meetingIsBreakout,
        }}
        isPrepareLesson={isPrepareLesson}
      />
    );
  }
  return null;
};

export default PresentationToolbarContainer;

PresentationToolbarContainer.propTypes = {
  // Number of current slide being displayed
  currentSlideNum: PropTypes.number.isRequired,

  // Total number of slides in this presentation
  numberOfSlides: PropTypes.number.isRequired,

  // Actions required for the presenter toolbar
  layoutSwapped: PropTypes.bool,
};
